// ─── Permission System ────────────────────────────────────────────────────────

export type { UserRole } from './rbacTypes';
import type { UserRole } from './rbacTypes';

// ─── Route Access Map ─────────────────────────────────────────────────────────
// super_admin: unrestricted
// admin: all operational + settings, no super-admin-only platform routes
// operator: operations only (vehicles, POs, inbound, receiving, repairs, transfers, tasks, dealer-transfers)

export const ROUTE_PERMISSIONS: Record<string, UserRole[]> = {
  // Super Admin ONLY
  '/super-admin-dashboard': ['super_admin'],
  '/tenants':               ['super_admin'],
  '/subscriptions':         ['super_admin'],
  '/roles':                 ['super_admin'],
  '/access-control':        ['super_admin'],
  '/billing':               ['super_admin'],
  '/clients':               ['super_admin'],
  '/client-users':          ['super_admin'],
  '/new-client-setup':      ['super_admin'],
  '/makes':                 ['super_admin'],

  // Admin + Super Admin
  '/users':                 ['super_admin', 'admin'],
  '/company-settings':      ['super_admin', 'admin'],
  '/bank-accounts':         ['super_admin', 'admin'],
  '/settings':              ['super_admin', 'admin'],
  '/locations':             ['super_admin', 'admin'],
  '/audit-logs':            ['super_admin', 'admin'],
  '/models':                ['super_admin', 'admin'],
  '/readiness-settings':    ['super_admin', 'admin'],
  '/settings/finance-institutions': ['super_admin', 'admin'],
  '/settings/config':       ['super_admin', 'admin'],
  '/stock-migration':       ['super_admin', 'admin'],
  '/stock-broadcast':        ['super_admin', 'admin', 'operator', 'sales'],

  // Finance — Admin + Super Admin
  '/additional-costs':      ['super_admin', 'admin'],
  '/import-flow':           ['super_admin', 'admin'],
  '/finance/profitability': ['super_admin', 'admin'],
  '/finance/receipts':      ['super_admin', 'admin', 'operator'],
  '/reports':               ['super_admin', 'admin'],

  // Sales — Admin + Super Admin
  '/sales-orders':          ['super_admin', 'admin', 'sales', 'operator'],
  '/pro-forma':             ['super_admin', 'admin', 'sales', 'operator'],
  '/authority-to-sell':     ['super_admin', 'admin', 'operator'],
  '/customers':             ['super_admin', 'admin', 'sales', 'operator'],
  '/salespersons':          ['super_admin', 'admin'],
  '/registrations':         ['super_admin', 'admin', 'operator'],
  '/handovers':             ['super_admin', 'admin', 'operator'],
  '/dealers':               ['super_admin', 'admin'],
  '/master-data':           ['super_admin', 'admin', 'operator'],

  // Add employee master route
  '/master-data/employees': ['super_admin', 'admin', 'operator'],

  // Operations — All roles
  '/dashboard':             ['super_admin', 'admin', 'operator'],
  '/dashboard/sales':       ['super_admin', 'admin'],
  '/vehicle-management':    ['super_admin', 'admin', 'operator', 'sales'],
  '/vehicle-management/sold': ['super_admin', 'admin'],
  '/purchase-orders':       ['super_admin', 'admin', 'operator'],
  '/inbound-tracking':      ['super_admin', 'admin', 'operator'],
  '/bill-of-ladings':       ['super_admin', 'admin', 'operator'],
  '/receiving':             ['super_admin', 'admin', 'operator'],
  '/repairs':               ['super_admin', 'admin', 'operator'],
  '/transfers':             ['super_admin', 'admin', 'operator'],
  '/tasks':                 ['super_admin', 'admin', 'operator'],
  '/dealer-transfers':      ['super_admin', 'admin', 'operator'],
  '/dealer-network':        ['super_admin', 'admin', 'operator'],
  '/document-upload':       ['super_admin', 'admin', 'operator'],
  '/master-data':           ['super_admin', 'admin', 'operator'],
  '/notifications':         ['super_admin', 'admin', 'operator', 'sales'],
  '/tasks/config/events':   ['super_admin', 'admin'],
  '/tasks/config/rules':    ['super_admin', 'admin'],
  '/tasks/config/groups':   ['super_admin', 'admin'],

  // Profile — all
  '/profile':               ['super_admin', 'admin', 'operator', 'sales'],
};

// ─── Nav Permissions ──────────────────────────────────────────────────────────

export const NAV_PERMISSIONS: Record<string, UserRole[]> = {
  '/dashboard':             ['super_admin', 'admin', 'operator'],
  '/dashboard/sales':       ['super_admin', 'admin'],
  '/vehicle-management':    ['super_admin', 'admin', 'operator', 'sales'],
  '/vehicle-management/sold': ['super_admin', 'admin'],
  '/purchase-orders':       ['super_admin', 'admin', 'operator'],
  '/inbound-tracking':      ['super_admin', 'admin', 'operator'],
  '/bill-of-ladings':       ['super_admin', 'admin', 'operator'],
  '/receiving':             ['super_admin', 'admin', 'operator'],
  '/repairs':               ['super_admin', 'admin', 'operator'],
  '/transfers':             ['super_admin', 'admin', 'operator'],
  '/tasks':                 ['super_admin', 'admin', 'operator'],
  '/sales-orders':          ['super_admin', 'admin', 'operator'],
  '/pro-forma':             ['super_admin', 'admin', 'operator'],
  '/authority-to-sell':     ['super_admin', 'admin', 'operator'],
  '/customers':             ['super_admin', 'admin', 'operator'],
  '/salespersons':          ['super_admin', 'admin'],
  '/registrations':         ['super_admin', 'admin', 'operator'],
  '/handovers':             ['super_admin', 'admin', 'operator'],
  '/dealer-transfers':      ['super_admin', 'admin', 'operator'],
  '/dealer-network/inbound-tracker': ['super_admin', 'admin', 'operator'],
  '/dealers':               ['super_admin', 'admin'],
  '/finance/profitability': ['super_admin', 'admin'],
  '/finance/receipts':      ['super_admin', 'admin', 'operator'],
  '/additional-costs':      ['super_admin', 'admin'],
  '/reports':               ['super_admin', 'admin'],
  '/document-upload':       ['super_admin', 'admin', 'operator'],
  '/notifications':         ['super_admin', 'admin', 'operator', 'sales'],
  '/tasks/config/events':   ['super_admin', 'admin'],
  '/tasks/config/rules':    ['super_admin', 'admin'],
  '/tasks/config/groups':   ['super_admin', 'admin'],
  '/users':                 ['super_admin', 'admin'],
  '/locations':             ['super_admin', 'admin'],
  '/company-settings':      ['super_admin', 'admin'],
  '/settings/config':       ['super_admin', 'admin'],
  '/stock-migration':       ['super_admin', 'admin'],
  '/stock-broadcast':        ['super_admin', 'admin', 'operator', 'sales'],
  '/bank-accounts':         ['super_admin', 'admin'],
  '/audit-logs':            ['super_admin', 'admin'],
  '/makes':                 ['super_admin'],
  '/models':                ['super_admin', 'admin'],
  '/super-admin-dashboard': ['super_admin'],
  '/subscriptions':         ['super_admin'],
  '/roles':                 ['super_admin'],
  '/access-control':        ['super_admin'],
  '/billing':               ['super_admin'],
  '/tenants':               ['super_admin'],
  '/clients':               ['super_admin'],
  '/client-users':          ['super_admin'],
  '/new-client-setup':      ['super_admin'],
  '/profile':               ['super_admin', 'admin', 'operator', 'sales'],
};

// ─── Feature Permissions ──────────────────────────────────────────────────────

export const FEATURE_PERMISSIONS = {
  // User management
  canManageUsers:       (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canInviteUsers:       (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canChangeRoles:       (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canDeactivateUsers:   (role: UserRole) => ['super_admin', 'admin'].includes(role),

  // Company settings
  canEditCompanySettings: (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canViewBilling:         (role: UserRole) => role === 'super_admin',
  canManageTenants:       (role: UserRole) => role === 'super_admin',
  canManageRoles:         (role: UserRole) => role === 'super_admin',
  canManageAccessControl: (role: UserRole) => role === 'super_admin',

  // Vehicles
  canAddVehicle:    (role: UserRole) => ['super_admin', 'admin', 'operator'].includes(role),
  canEditVehicle:   (role: UserRole) => ['super_admin', 'admin', 'operator'].includes(role),
  canDeleteVehicle: (role: UserRole) => ['super_admin', 'admin'].includes(role),

  // Orders
  canCreateSalesOrder:    (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canApproveSalesOrder:   (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canCreatePurchaseOrder: (role: UserRole) => ['super_admin', 'admin', 'operator'].includes(role),
  canApprovePurchaseOrder:(role: UserRole) => ['super_admin', 'admin'].includes(role),
  canRejectPurchaseOrder: (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canReceivePurchaseOrder:(role: UserRole) => ['super_admin', 'admin', 'operator'].includes(role),
  canUpdateInboundStatus: (role: UserRole) => ['super_admin', 'admin', 'operator'].includes(role),

  // Customers
  canCreateCustomer: (role: UserRole) => ['super_admin', 'admin', 'manager', 'operator'].includes(role),
  canEditCustomer:   (role: UserRole) => ['super_admin', 'admin', 'manager', 'operator'].includes(role),
  canDeleteCustomer: (role: UserRole) => ['super_admin', 'admin'].includes(role),

  // Finance
  canViewFinance:    (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canEditFinance:    (role: UserRole) => ['super_admin', 'admin'].includes(role),
  canViewCostFields: (role: UserRole) => ['super_admin', 'admin'].includes(role),

  // Transfers
  canCreateTransfer: (role: UserRole) => ['super_admin', 'admin', 'operator'].includes(role),
  canApproveTransfer:(role: UserRole) => ['super_admin', 'admin'].includes(role),
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function hasRouteAccess(role: UserRole | null | undefined, pathname: string): boolean {
  if (!role) return false;
  if (role === 'super_admin') return true;
  // 'manager' is not in the UserRole type; managers ticked under "Who may send" may open the broadcast screens (the pages check the tick).
  if (pathname.startsWith('/stock-broadcast') && (role as string) === 'manager') return true;

  const matchedRoute = Object.keys(ROUTE_PERMISSIONS)
    .filter(route => pathname.startsWith(route))
    .sort((a, b) => b.length - a.length)[0];

  if (!matchedRoute) return true;
  return ROUTE_PERMISSIONS[matchedRoute].includes(role);
}

export function hasNavAccess(role: UserRole | null | undefined, href: string): boolean {
  if (!role) return false;
  if (role === 'super_admin') return true;
  const allowed = NAV_PERMISSIONS[href];
  if (!allowed) return true;
  return allowed.includes(role);
}

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  operator: 'Operator',
  sales: 'Sales',
  manager: 'Manager',
  viewer: 'Viewer',
};

export const ROLE_COLORS: Record<UserRole, { text: string; bg: string; border: string }> = {
  super_admin: { text: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200' },
  admin:       { text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  operator:    { text: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200' },
  sales:       { text: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
  manager:     { text: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  viewer:      { text: 'text-slate-700',  bg: 'bg-slate-50',  border: 'border-slate-200' },
};
